import React, { useEffect, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Link as RouterLink } from "react-router-dom";
import { TrendingUp, MousePointerClick, Users, Globe2, ArrowUpRight, UserCircle2, ArrowRight } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Area, AreaChart } from "recharts";

export default function DashboardOverview() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/dashboard/overview?days=14");
        setData(data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <DashboardLayout>
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="label-eyebrow text-[#002FA7]">Overview</div>
          <h1 className="text-4xl sm:text-5xl tracking-tight font-black text-[#111827]">Hello, {user?.name?.split(" ")[0]}</h1>
        </div>
        <RouterLink to="/dashboard/links/new" data-testid="overview-create-link-cta" className="btn-primary">+ New link</RouterLink>
      </div>

      <ProfileCompletionBanner user={user} />

      {loading ? (
        <SkeletonStats />
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
            <StatCard testid="stat-total-clicks" label="Total clicks" value={data.total_clicks} icon={MousePointerClick} />
            <StatCard testid="stat-unique-clicks" label="Unique clicks" value={data.unique_clicks} icon={TrendingUp} />
            <StatCard testid="stat-total-leads" label="Leads captured" value={data.total_leads} icon={Users} />
            <StatCard testid="stat-conversions" label="Conversions" value={data.total_conversions || 0} icon={ArrowUpRight} accent />
            <StatCard testid="stat-conv-rate" label="Conv. rate" value={`${data.conversion_rate}%`} icon={ArrowUpRight} />
          </div>

          <div className="grid lg:grid-cols-3 gap-4 mb-6">
            <div className="card-flat p-6 lg:col-span-2">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="label-eyebrow text-[#4B5563]">Last 14 days</div>
                  <div className="font-display font-extrabold text-xl">Clicks & leads</div>
                </div>
                <div className="flex items-center gap-3 text-xs">
                  <span className="inline-flex items-center gap-1 text-[#111827]"><span className="h-2 w-2 rounded-full bg-[#111827]" /> Clicks</span>
                  <span className="inline-flex items-center gap-1 text-[#002FA7]"><span className="h-2 w-2 rounded-full bg-[#002FA7]" /> Leads</span>
                </div>
              </div>
              <div className="h-64" data-testid="overview-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={data.series} margin={{ left: -20, right: 8, top: 8, bottom: 0 }}>
                    <defs>
                      <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#111827" stopOpacity={0.18} />
                        <stop offset="100%" stopColor="#111827" stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="g2" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#002FA7" stopOpacity={0.25} />
                        <stop offset="100%" stopColor="#002FA7" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid stroke="#F3F4F6" vertical={false} />
                    <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#4B5563" }} tickFormatter={(v) => v.slice(5)} />
                    <YAxis tick={{ fontSize: 11, fill: "#4B5563" }} allowDecimals={false} />
                    <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E5E7EB", fontSize: 12 }} />
                    <Area type="monotone" dataKey="clicks" stroke="#111827" strokeWidth={2} fill="url(#g1)" />
                    <Area type="monotone" dataKey="leads" stroke="#002FA7" strokeWidth={2} fill="url(#g2)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="card-flat p-6">
              <div className="flex items-center gap-2 mb-4">
                <Globe2 size={16} className="text-[#002FA7]" />
                <div className="font-display font-extrabold text-xl">By country</div>
              </div>
              <div className="space-y-3" data-testid="countries-list">
                {data.countries.length === 0 && (
                  <div className="text-sm text-[#4B5563]">No clicks yet — share a link to see countries appear here.</div>
                )}
                {data.countries.map((c) => (
                  <div key={c.country_code + c.country} className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="font-mono text-xs bg-[#F3F4F6] rounded px-2 py-0.5">{c.country_code}</span>
                      <span className="text-sm font-medium text-[#111827] truncate">{c.country}</span>
                    </div>
                    <span className="font-display font-black text-sm">{c.count}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="card-flat p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="label-eyebrow text-[#4B5563]">Recent leads</div>
                <div className="font-display font-extrabold text-xl">Captured customers</div>
              </div>
              <RouterLink to="/dashboard/leads" data-testid="overview-view-all-leads" className="text-sm font-bold text-[#002FA7] hover:underline">View all →</RouterLink>
            </div>
            <LeadsTable leads={data.recent_leads || []} />
          </div>
        </>
      )}
    </DashboardLayout>
  );
}

function StatCard({ label, value, icon: Icon, accent, testid }) {
  return (
    <div className="card-flat p-5" data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="label-eyebrow text-[#4B5563]">{label}</div>
        <div className={`h-8 w-8 rounded-md flex items-center justify-center ${accent ? "bg-[#002FA7] text-white" : "bg-[#F3F4F6] text-[#111827]"}`}>
          <Icon size={14} />
        </div>
      </div>
      <div className="mt-3 font-display font-black text-3xl tracking-tight">{value}</div>
    </div>
  );
}

function ProfileCompletionBanner({ user }) {
  if (!user) return null;
  const missing = [];
  if (!user.picture) missing.push("photo");
  if (!user.phone) missing.push("phone");
  if (!user.address) missing.push("address");
  if (missing.length === 0) return null;

  return (
    <div
      className="mb-6 rounded-lg border border-[#BFDBFE] bg-[#EFF6FF] p-5 flex items-center gap-4 fade-up"
      data-testid="profile-completion-banner"
    >
      <div className="hidden sm:flex h-12 w-12 rounded-full bg-[#002FA7] text-white items-center justify-center shrink-0">
        <UserCircle2 size={22} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-display font-extrabold text-[#111827]">Complete your affiliate profile</div>
        <p className="text-sm text-[#4B5563] mt-1">
          Add your <span className="font-bold text-[#111827]">{missing.join(", ")}</span> so customers know exactly who's referring them.
        </p>
      </div>
      <RouterLink
        to="/dashboard/profile"
        data-testid="profile-banner-cta"
        className="btn-primary shrink-0"
      >
        Set up profile <ArrowRight size={14} />
      </RouterLink>
    </div>
  );
}

function SkeletonStats() {
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
      {[0,1,2,3].map((i) => (<div key={i} className="card-flat p-5 h-28 animate-pulse bg-[#F9FAFB]" />))}
    </div>
  );
}

export function LeadsTable({ leads }) {
  if (!leads || leads.length === 0) {
    return <div className="text-sm text-[#4B5563] py-6 text-center">No leads yet. Share your links to start collecting.</div>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-left text-sm" data-testid="leads-table">
        <thead className="text-[#4B5563]">
          <tr className="border-b border-[#E5E7EB]">
            <th className="label-eyebrow font-bold py-3 pr-3">Name</th>
            <th className="label-eyebrow font-bold py-3 pr-3">Email</th>
            <th className="label-eyebrow font-bold py-3 pr-3">Phone</th>
            <th className="label-eyebrow font-bold py-3 pr-3 hidden md:table-cell">Delivery</th>
            <th className="label-eyebrow font-bold py-3 pr-3 hidden md:table-cell">Country</th>
            <th className="label-eyebrow font-bold py-3 pr-3">Discount</th>
            <th className="label-eyebrow font-bold py-3 pr-3 hidden lg:table-cell">When</th>
          </tr>
        </thead>
        <tbody>
          {leads.map((l) => (
            <tr key={l.lead_id} className="border-b border-[#F3F4F6] hover:bg-[#F9FAFB]">
              <td className="py-3 pr-3 font-bold text-[#111827]">{l.name}</td>
              <td className="py-3 pr-3 text-[#4B5563]">{l.email}</td>
              <td className="py-3 pr-3 text-[#4B5563] font-mono text-xs">{l.phone || "—"}</td>
              <td className="py-3 pr-3 text-[#4B5563] hidden md:table-cell">{l.delivery_location}</td>
              <td className="py-3 pr-3 hidden md:table-cell">
                <span className="font-mono text-xs bg-[#F3F4F6] rounded px-2 py-0.5">{l.country_code || "??"}</span>{" "}
                <span className="text-[#4B5563]">{l.country || "Unknown"}</span>
              </td>
              <td className="py-3 pr-3">
                {l.wants_discount ? (
                  <span className="inline-flex items-center rounded-full bg-[#002FA7] text-white text-xs font-bold px-2.5 py-0.5">YES</span>
                ) : (
                  <span className="inline-flex items-center rounded-full bg-[#F3F4F6] text-[#4B5563] text-xs font-bold px-2.5 py-0.5">NO</span>
                )}
              </td>
              <td className="py-3 pr-3 text-xs text-[#4B5563] hidden lg:table-cell">{new Date(l.created_at).toLocaleString()}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
